import {useEffect, useState} from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Box,
} from '@mui/material';
import {DatePicker} from '@/components/common/DatePicker';
import {MoneyField} from '@/components/common/MoneyField';
import {useAdjustmentStore} from '@/store/audit/adjustmentStore';

export interface MotorHireRow {
    date: string;
    item: string;
    cost: number;
    hpBf: number;
    downPayment: number;
    hpCf: number;
    hpAmount?: number;
    repaymentsForPeriod?: number;
    ia?: number;
}

interface MotorHireTableProps {
    parentCurrency: string | null;
    storeRow?: MotorHireRow[];
}

export function MotorHireTable({
                                   parentCurrency,
                                   storeRow: propStoreRow,
                               }: MotorHireTableProps) {
    const getAllMotorHire = useAdjustmentStore((state) => state.getAllMotorHire);
    const [storeRow, setStoreRow] = useState<MotorHireRow[]>(propStoreRow || []);

    useEffect(() => {
        if (propStoreRow) {
            setStoreRow(propStoreRow);
            return;
        }

        const motorHireData = getAllMotorHire();
        setStoreRow(motorHireData);
    }, [getAllMotorHire, propStoreRow]);

    if (!storeRow || storeRow.length === 0) {
        return null;
    }

    // Calculate totals
    const totals = storeRow.reduce(
        (acc, row) => {
            const repayments = row.repaymentsForPeriod ?? (row.hpBf - row.hpCf);
            const hpCf = row.hpCf ?? (row.hpBf - repayments);
            const hpAmount = row.hpAmount ?? ((row.cost ?? 0) + row.downPayment);
            const ia60 = row.ia ?? (repayments * 0.6);
            return {
                cost: acc.cost + row.cost,
                hpBf: acc.hpBf + row.hpBf,
                downPayment: acc.downPayment + row.downPayment,
                hpAmount: acc.hpAmount + hpAmount,
                repayments: acc.repayments + repayments,
                hpCf: acc.hpCf + hpCf,
                ia60: acc.ia60 + ia60,
            };
        },
        {
            cost: 0,
            hpBf: 0,
            downPayment: 0,
            hpAmount: 0,
            repayments: 0,
            hpCf: 0,
            ia60: 0,
        }
    );

    const showTotals = storeRow.length > 1;

    return (
        <Box>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={10}>
                            <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                                Obligation Under Finance Leases
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{fontWeight: 'bold'}} colSpan={3}/>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            Cost
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            HP b/f
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            DownPayment
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            HP amount
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            Repayments <br /> (for the period)
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            HP c/f
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            I.A. (60%)
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {storeRow.map((row, index) => {
                        const hpAmount = row.hpAmount ?? ((row.cost ?? 0) + row.downPayment);
                        const repayments = row.repaymentsForPeriod ?? (row.hpBf - row.hpCf);
                        const hpCf = row.hpCf ?? (row.hpBf - repayments);
                        const ia60 = row.ia ?? (repayments * 0.6);

                        return (
                            <TableRow key={index}>
                                <TableCell sx={{minWidth: '20px'}}>{index + 1}</TableCell>
                                <TableCell sx={{minWidth: '100px'}}>
                                    <DatePicker
                                        value={row.date}
                                        onChange={() => {
                                        }}
                                        display={true}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell sx={{minWidth: '100px'}}>
                                    <Typography variant="body2" sx={{fontWeight: 'bold'}}>
                                        {row.item}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.cost}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.hpBf}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.downPayment}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={hpAmount}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={repayments}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={hpCf}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={ia60}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {showTotals && (
                        <TableRow sx={{backgroundColor: '#f5f5f5'}}>
                            <TableCell colSpan={3} sx={{fontWeight: 'bold'}}>
                                Total
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.cost}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.hpBf}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.downPayment}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.hpAmount}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.repayments}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.hpCf}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.ia60}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <TableCell colSpan={8} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={8} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );
}
